import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { fbt } from 'fbtee'
import { TICKETS_MAX } from '@shared/eventTickets'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import { DashedRow } from '../../../components/LinkGroupList'
import { AudiencePicker } from '../../../components/AudiencePicker'
import { useAudienceSelection } from '../../../hooks/useAudienceSelection'
import { useUserProfilesWithLoading } from '../../../hooks/useUserProfiles'
import { useBlockedUsers } from '../../../contexts/BlockedUsersContext'
import { useFavoriteIdSet } from '../../../contexts/FavoriteFriendsContext'
import { saveEventTickets } from '../../../hooks/useEventTickets'
import { cancelButtonLabel, saveButtonLabel } from '../../../lib/commonStrings'
import { TicketSlotRow, type DraftSlot } from './TicketSlotRow'
import type { Event, EventTickets, Group } from '../../../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: Event
  userId: string
  mine: EventTickets | null
  /** Friends and group members the viewer can give a ticket to. */
  candidates: string[]
  /** Groups the owner is a member of — the audience channels besides Friends. */
  groups: Group[]
  seekerIds: ReadonlySet<string>
  goingUserIds: ReadonlySet<string>
  interestedUserIds: ReadonlySet<string>
}

/** The owner's editor: how many tickets they bought and who each one is for. */
export function TicketLedgerDialog({ open, onOpenChange, mine, ...form }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{fbt('Your tickets', 'Title of the dialog where the viewer records the tickets they bought for an event')}</DialogTitle>
          <DialogDescription>
            {fbt(
              'Who is each ticket for? People you pick confirm it, and your crew can claim open ones.',
              'Subtitle of the tickets dialog explaining assigning and open tickets',
            )}
          </DialogDescription>
        </DialogHeader>
        {/* Keyed on the version so a claim landing mid-edit re-seeds the draft. */}
        {open && <LedgerForm key={mine?.version ?? 0} {...form} mine={mine} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function LedgerForm({ event, userId, mine, candidates, groups, seekerIds, goingUserIds, interestedUserIds, onClose }: Omit<Props, 'open' | 'onOpenChange'> & { onClose: () => void }) {
  const [draft, setDraft] = useState<DraftSlot[]>(() => (mine?.tickets.length ? mine.tickets.map(s => ({ ...s })) : [{ kind: 'me' }]))
  const [saving, setSaving] = useState(false)
  const audience = useAudienceSelection(groups, mine ?? undefined)
  const { isBlocked } = useBlockedUsers()

  // Everyone the saved ledger already names stays pickable, even if they've left the crew.
  const people = useMemo(() => {
    const assigned = (mine?.tickets ?? []).flatMap(s => (s.kind === 'user' && s.userId ? [s.userId] : []))
    return [...new Set([...candidates.filter(id => id !== userId && !isBlocked(id)), ...assigned])]
  }, [candidates, mine, userId, isBlocked])
  const profileIds = useMemo(() => [userId, ...people], [userId, people])
  const { profiles } = useUserProfilesWithLoading(profileIds)
  const favoriteIdSet = useFavoriteIdSet()
  // Looking for a ticket first, always; then the order facepiles use — going,
  // interested, the rest — with favorites first within each, then A–Z.
  const sortedPeople = useMemo(() => {
    const rank = (id: string) => (seekerIds.has(id) ? 0 : goingUserIds.has(id) ? 1 : interestedUserIds.has(id) ? 2 : 3)
    const name = (id: string) => profiles[id]?.displayName ?? ''
    return [...people].sort((a, b) =>
      rank(a) - rank(b) || Number(favoriteIdSet.has(b)) - Number(favoriteIdSet.has(a)) || name(a).localeCompare(name(b)))
  }, [people, profiles, seekerIds, goingUserIds, interestedUserIds, favoriteIdSet])
  const named = draft.flatMap(s => (s.kind === 'user' && s.userId ? [s.userId] : []))

  const meTaken = draft.some(s => s.kind === 'me')
  const invalid = draft.some(s => s.kind === 'name' && !s.name?.trim())

  async function save() {
    if (saving || invalid || audience.count === 0) return
    setSaving(true)
    const tickets = draft.map(({ id, kind, userId: uid, name }) => ({
      ...(id ? { id } : {}), kind, ...(kind === 'user' ? { userId: uid } : {}), ...(kind === 'name' ? { name: name?.trim() } : {}),
    }))
    const result = await saveEventTickets({ eventId: event.id, tickets, version: mine?.version ?? 0, setAudience: true, ...audience.value })
    setSaving(false)
    if (result) onClose()
  }

  return (
    <form className="space-y-4" onSubmit={e => { e.preventDefault(); save() }}>
      <fieldset disabled={saving} className="min-w-0 space-y-4 transition-opacity disabled:opacity-60">
        <div className="space-y-2">
          <ul className="space-y-2">
            {draft.map((slot, i) => (
              <TicketSlotRow
                key={slot.id ?? `new-${i}`}
                index={i}
                slot={slot}
                selfProfile={profiles[userId]}
                people={sortedPeople}
                profiles={profiles}
                seekerIds={seekerIds}
                goingUserIds={goingUserIds}
                interestedUserIds={interestedUserIds}
                taken={new Set(named.filter(id => !(slot.kind === 'user' && slot.userId === id)))}
                meTaken={meTaken}
                onChange={next => setDraft(d => d.map((s, j) => (j === i ? next : s)))}
                onRemove={() => setDraft(d => d.filter((_, j) => j !== i))}
              />
            ))}
          </ul>
          {draft.length < TICKETS_MAX && (
            <DashedRow
              onClick={() => setDraft(d => [...d, { kind: 'open' }])}
              label={fbt('Add a ticket', 'Dashed row in the tickets dialog adding one more ticket')}
              className="h-[42px] py-0"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {fbt('Visible to', 'Label before the audience picker in the tickets dialog')}
          </span>
          <AudiencePicker
            groups={groups}
            selection={audience}
            triggerLabel={audience.isEveryone
              ? fbt('All your crew', "Tickets audience picker label when every group and friends can see the viewer's tickets")
              : fbt(
                  fbt.plural('channel', audience.count, { showCount: 'yes', many: 'channels' }),
                  "Tickets audience picker label showing how many groups/friends can see the viewer's tickets",
                )}
            header={fbt('Who sees your tickets?', 'Header of the audience picker in the tickets dialog')}
            friendsLabel={fbt('Friends', "Audience option letting the viewer's friends see their tickets")}
          />
        </div>
        </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>{cancelButtonLabel()}</Button>
        <Button type="submit" disabled={saving || invalid || audience.count === 0}>
          {saving && <Loader2 className="size-4 animate-spin" />}
          {saveButtonLabel()}
        </Button>
      </div>
    </form>
  )
}
